import {
  faFileExport,
  faFilter,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import avatar from 'assets/img/team/150x150/avatar.webp';
import SearchBox from 'components/common/SearchBox';
import LeadsFilterModal from 'components/modals/LeadsFilterModal';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';

import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useState, ChangeEvent, useEffect } from 'react';
import { Card, Col, Row, Toast, ToastContainer } from 'react-bootstrap';
import apiClient from 'helpers/apiService';
import { useAuth } from 'providers/AuthProvider';
import { ContactData } from './components/DataDefinitions';
import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import Avatar from 'components/base/Avatar';
import FeatherIcon from 'feather-icons-react';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { useNavigate } from "react-router-dom";
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const ActionDropdownItems = ({ contact_id, onDeleteContact }: {
  contact_id: string | number,
  onDeleteContact: (contact_id: string | number) => void;
}) => {
  return (
    <>
      <Dropdown.Item eventKey="1" onClick={() => onDeleteContact(contact_id)} className="text-danger">
        Remove
      </Dropdown.Item>
    </>
  );
};

const ContactsTable = () => {
  return (
    <div className="border-top">
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger"
      />
      <AdvanceTableFooter pagination className="py-4" />
    </div>
  );
};



const Contacts = () => {
  const auth = useAuth();
  const [contacts, setContacts] = useState<ContactData[]>([]);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  // State to control Toast messages
  const [show, setShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const breadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: t('Dashboard'),
      url: '/dashboard/home'
    },
    {
      label: t('Contacts.page_title'),
      active: true
    }
  ];

  const contactsTableColumns: ColumnDef<ContactData>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row: { original } }) => {
        const {
          picture,
          name,
          role
        } = original;

        //const contact_image = picture ? `data:image/jpeg;base64, ${picture}` : avatar;
        const contact_image = picture ? picture : avatar;
        return (

          <div className="d-flex align-items-center">
            <Link to={`/contacts/update-contact?contact_id=${original.contact_id}`}>

              <Avatar src={contact_image} size="xl" className="me-3" />
            </Link>
            <div>
              <Link to={`/contacts/update-contact?contact_id=${original.contact_id}`} className="fs-8 fw-bold">
                {name}
              </Link>
              <div className="d-flex align-items-center">
                <p className="mb-0 text-1000 fw-semi-bold fs-9 me-2">
                  {role}
                </p>
              </div>
            </div>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '25%' }, className: 'ps-0' },
        cellProps: { className: 'white-space-nowrap ps-0' }
      }
    },
    {
      accessorKey: 'email',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-success-100 rounded me-2">
              <FeatherIcon
                icon="mail"
                size={16}
                className="text-success-600 dark__text-success-300"
              />
            </div>
            <span>Email</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { email } = original;
        return (
          <Link className="text-1000" to={`mailto:${email}`}>
            {email}
          </Link>
        );
      },
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
        }
      }
    },
    {
      accessorKey: 'phone',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-primary-100 rounded me-2">
              <FeatherIcon
                icon="phone"
                size={16}
                className="text-primary-600 dark__text-primary-300"
              />
            </div>
            <span>Phone</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { phone } = original;
        return (
          <Link className="text-1000" to={`tel:${phone}`}>
            {phone}
          </Link>
        );
      },
      meta: {
        headerProps: {
          style: { width: '15%', minWidth: '180px' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
        }
      }
    },
    {
      accessorKey: 'company_name',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-warning-100 rounded me-2">
              <FeatherIcon
                icon="grid"
                size={16}
                className="text-warning-600 dark__text-warning-300"
              />
            </div>
            <span>Company name</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => original.company_name,
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap ps-4 border-end fw-semi-bold text-1000'
        }
      }
    },
    {
      accessorKey: 'inserted_at',
      header: 'Create date',
      cell: ({ row: { original } }) => original.inserted_at,
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'ps-4 pe-5'
        },
        cellProps: {
          className: 'white-space-nowrap ps-4 text-700'
        }
      }
    },
    {
      id: 'contactDropdown',
      cell: ({ row: { original } }) => {
        return (
          <RevealDropdownTrigger>
            <RevealDropdown>
              <ActionDropdownItems contact_id={original.contact_id} onDeleteContact={deleteContact} />
            </RevealDropdown>
          </RevealDropdownTrigger>
        );
      },
      meta: {
        cellProps: {
          className: 'text-end pe-0 ps-4'
        }
      }
    }
  ];

  const fetchData = async () => {
    try {
      const response = await apiClient.get('/v1/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);


  const table = useAdvanceTable({
    data: contacts,
    columns: contactsTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true,
    initialState: {
      columnVisibility: {
        status: false,
        designation: false
      }
    }
  });
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const navigate = useNavigate();
  const handleClick = () => {
    navigate('/contacts/add-contact'); // Navigate to the AddContact page
  };

  const deleteContact = async (contact_id: string | number) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      const response = await apiClient.delete(`/v1/contacts/${contact_id}`);
      setToastMessage(response.data['message'])
      setShow(true);
      fetchData();
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <PageBreadcrumb items={breadcrumbItems} />

      <ToastContainer className="p-3" position='top-center'>
        <Toast onClose={() => setShow(false)} show={show} delay={3000} autohide>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Card className='mt-3  mb-3 border border-light rounded-5' >
        <Card.Body>
          <AdvanceTableProvider {...table}>
            <Row className="g-3 justify-content-between mb-4">
              <Col xs="auto">
                <Button
                  variant="primary"
                  className="me-4"
                  startIcon={
                    <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                  }
                  onClick={handleClick}
                >
                  {t('Contacts.add_contact')}
                </Button>

                <Button
                  variant="link"
                  className="text-900 px-0"
                  startIcon={
                    <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                  }
                >
                  {t("Contacts.export")}
                </Button>
              </Col>
              <Col xs="auto">
                <div className="d-flex">
                  <SearchBox
                    placeholder="Search by name"
                    className="me-2"
                    onChange={handleSearchInputChange}
                  />
                  <DatePicker defaultValue="Mar 1, 2022" />
                  <Button
                    variant="phoenix-secondary"
                    className="px-3 ms-2"
                    onClick={() => setOpenFilterModal(true)}
                  >
                    <FontAwesomeIcon
                      icon={faFilter}
                      transform="down-3"
                      className="text-primary"
                    />
                  </Button>
                </div>
                <LeadsFilterModal
                  show={openFilterModal}
                  handleClose={() => setOpenFilterModal(false)}
                />
              </Col>
            </Row>
            <ContactsTable />
          </AdvanceTableProvider>
        </Card.Body>
      </Card>
    </>
  );
};

export default Contacts;